import { useMemo, useState, type ReactNode } from "react";

import { cn } from "../../lib/cn";
import { useInteraction } from "../../lib/use-interaction";
import { Button } from "../button";
import { Checkbox } from "../checkbox";
import { EmptyState } from "../empty-state";
import { StatusBadge, type CloudStatus } from "./status";

export interface ResourceColumn<T> {
  key: string;
  header: ReactNode;
  /** Contenu de la cellule. */
  cell: (row: T) => ReactNode;
  align?: "start" | "end";
  /** Rend la colonne triable. Renvoie la valeur de comparaison. */
  sortBy?: (row: T) => string | number;
  /**
   * Importance de la colonne quand la place manque.
   *
   * `primary`   → titre de la fiche compacte (une seule colonne devrait l'être) ;
   * `secondary` → affichée sous le titre ;
   * `detail`    → masquée en fiche compacte. Une console a souvent quinze
   *               colonnes ; les empiler toutes sur un écran de téléphone
   *               donne une fiche de deux écrans de haut que personne ne lit.
   */
  priority?: "primary" | "secondary" | "detail";
}

export interface ResourceTableProps<T> {
  rows: T[];
  columns: ResourceColumn<T>[];
  /** Identifiant stable d'une ligne. Sert à la sélection et aux clés React. */
  rowId: (row: T) => string;
  /** État de la ressource, affiché en pastille dans la fiche compacte. */
  rowStatus?: (row: T) => CloudStatus;
  /** Active la sélection multiple et la barre d'actions groupées. */
  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  /** Actions groupées, affichées dès qu'une ligne est cochée. */
  bulkActions?: (selectedIds: string[]) => ReactNode;
  /** Actions par ligne (menu, arrêt, redémarrage…). */
  rowActions?: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  emptyState?: ReactNode;
  className?: string;
}

/**
 * Inventaire de ressources pour console Cloud.
 *
 * ── Garanties d'alignement & Ergonomie ────────────────────────────────────
 * - Alignement strict et parfait entre la colonne Checkbox de l'en-tête et du corps.
 * - En-tête gris continu sur 100% de la largeur du tableau.
 * - Gestion propre des colonnes de données et d'actions.
 */
export function ResourceTable<T>({
  rows,
  columns,
  rowId,
  rowStatus,
  selectable = false,
  selectedIds,
  onSelectionChange,
  bulkActions,
  rowActions,
  onRowClick,
  emptyState,
  className,
}: ResourceTableProps<T>) {
  const { isTouch, canHover, isNarrow } = useInteraction();
  const [internalSelection, setInternalSelection] = useState<string[]>([]);
  const [sort, setSort] = useState<{ key: string; desc: boolean } | null>(null);

  const selection = selectedIds ?? internalSelection;
  const setSelection = (ids: string[]) => {
    if (selectedIds === undefined) setInternalSelection(ids);
    onSelectionChange?.(ids);
  };

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortBy) return rows;

    return [...rows].sort((a, b) => {
      const va = column.sortBy!(a);
      const vb = column.sortBy!(b);
      const delta =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb), "fr", { numeric: true });
      return sort.desc ? -delta : delta;
    });
  }, [rows, columns, sort]);

  const allSelected = rows.length > 0 && selection.length === rows.length;

  const toggleAll = () => setSelection(allSelected ? [] : rows.map(rowId));
  const toggleOne = (id: string) =>
    setSelection(selection.includes(id) ? selection.filter((s) => s !== id) : [...selection, id]);

  const toggleSort = (key: string) =>
    setSort((prev) =>
      prev?.key === key ? (prev.desc ? null : { key, desc: true }) : { key, desc: false },
    );

  if (rows.length === 0) {
    return (
      <div className={cn("rounded-xl border border-border bg-surface-raised", className)}>
        {emptyState ?? (
          <EmptyState
            title="Aucune ressource"
            description="Rien à afficher pour les filtres courants."
          />
        )}
      </div>
    );
  }

  const bulkBar = selectable && selection.length > 0 && (
    <div className="flex flex-wrap items-center gap-3 border-b border-border bg-accent-subtle/50 px-4 py-2.5">
      <span className="text-sm font-medium text-accent-fg">
        {selection.length} sélectionnée{selection.length > 1 ? "s" : ""}
      </span>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {bulkActions?.(selection)}
        <Button size="sm" variant="ghost" onClick={() => setSelection([])}>
          Annuler
        </Button>
      </div>
    </div>
  );

  /* Mode tactile / écran étroit : Fiches compactes */
  if (isTouch || isNarrow) {
    const primary = columns.find((c) => c.priority === "primary") ?? columns[0]!;
    const secondary = columns.filter((c) => c.priority === "secondary");

    return (
      <div
        className={cn(
          "overflow-hidden rounded-xl border border-border bg-surface-raised not-selectable",
          className,
        )}
      >
        {bulkBar}

        <ul className="divide-y divide-border">
          {sortedRows.map((row) => {
            const id = rowId(row);
            const isSelected = selection.includes(id);

            return (
              <li
                key={id}
                className={cn(
                  "flex items-start gap-3 px-4 py-3.5",
                  isSelected && "bg-accent-subtle/40",
                )}
              >
                {selectable && (
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleOne(id)}
                    aria-label={`Sélectionner ${id}`}
                    className="mt-1 tap-target"
                  />
                )}

                <button
                  type="button"
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  disabled={!onRowClick}
                  className="min-w-0 flex-1 text-left disabled:cursor-default"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 truncate font-medium text-fg">{primary.cell(row)}</div>
                    {rowStatus && <StatusBadge status={rowStatus(row)} />}
                  </div>

                  {secondary.length > 0 && (
                    <dl className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1">
                      {secondary.map((column) => (
                        <div key={column.key} className="min-w-0">
                          <dt className="text-[11px] tracking-wide text-fg-subtle uppercase">
                            {column.header}
                          </dt>
                          <dd className="truncate text-sm text-fg-muted selectable">
                            {column.cell(row)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </button>

                {rowActions && <div className="flex shrink-0 items-center gap-1">{rowActions(row)}</div>}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  /* Mode pointeur : Vrai Tableau avec colonnes rigoureusement alignées */
  return (
    <div className={cn("overflow-hidden rounded-xl border border-border bg-surface-raised shadow-xs", className)}>
      {bulkBar}

      <div
        tabIndex={0}
        role="region"
        aria-label="Inventaire des ressources"
        className="overflow-x-auto scrollbar-thin focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
      >
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-border bg-surface-sunken text-xs tracking-wide text-fg-muted uppercase">
              {/* Colonne Checkbox : Colonne 1 de l'en-tête */}
              {selectable && (
                <th scope="col" className="w-12 bg-surface-sunken px-4 py-3 text-center align-middle">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selection.length > 0 && !allSelected}
                    onChange={toggleAll}
                    aria-label="Tout sélectionner"
                  />
                </th>
              )}

              {/* Colonnes de données : Colonnes 2 à N */}
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    "bg-surface-sunken px-4 py-3 font-semibold whitespace-nowrap align-middle",
                    column.align === "end" && "text-right",
                  )}
                >
                  {column.sortBy ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className={cn(
                        "inline-flex items-center gap-1 uppercase transition-colors duration-fast hover:text-fg focus-visible:outline-2 focus-visible:outline-ring cursor-pointer",
                        column.align === "end" && "w-full justify-end",
                      )}
                      aria-sort={
                        sort?.key === column.key
                          ? sort.desc
                            ? "descending"
                            : "ascending"
                          : "none"
                      }
                    >
                      {column.header}
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        className={cn(
                          "size-3 transform-gpu transition-[transform,opacity] duration-fast ease-standard",
                          sort?.key === column.key ? "opacity-100" : "opacity-30",
                          sort?.key === column.key && sort.desc && "rotate-180",
                        )}
                      >
                        <path d="m6 8 4 4 4-4" />
                      </svg>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}

              {/* Colonne d'Actions par ligne : Colonne finale */}
              {rowActions && (
                <th scope="col" className="bg-surface-sunken px-4 py-3 text-right font-semibold align-middle">
                  <span>Actions</span>
                </th>
              )}
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {sortedRows.map((row) => {
              const id = rowId(row);
              const isSelected = selection.includes(id);

              return (
                <tr
                  key={id}
                  aria-selected={isSelected}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    "group transition-colors hover:bg-surface-sunken/40",
                    isSelected && "bg-accent-subtle/40",
                    onRowClick && "cursor-pointer",
                  )}
                >
                  {/* Cellule Checkbox : Colonne 1 du corps */}
                  {selectable && (
                    <td className="w-12 px-4 py-3 text-center align-middle" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleOne(id)}
                        aria-label={`Sélectionner ${id}`}
                      />
                    </td>
                  )}

                  {/* Cellules de données : Colonnes 2 à N */}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        "px-4 py-3 text-fg align-middle",
                        column.align === "end" && "text-right tabular-nums",
                      )}
                    >
                      {column.cell(row)}
                    </td>
                  ))}

                  {/* Cellule d'Actions : Colonne finale */}
                  {rowActions && (
                    <td className="px-4 py-3 text-right align-middle" onClick={(event) => event.stopPropagation()}>
                      <div
                        className={cn(
                          "flex items-center justify-end gap-1",
                          canHover && "opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-fast",
                        )}
                      >
                        {rowActions(row)}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
